import React from 'react';
import {Alert, Button, Card, Divider, Flex, Form, message, Modal, Switch, Typography} from "antd";
import {useAuthContext} from "../launcher/auth/LauncherAuthContext";
import {defaultFormItemLayout} from "../commons/forms";
import {useTranslation} from "react-i18next";
import axios, {AxiosResponse} from "axios";
import {LoadingOverlay} from "../launcher/common/loading/component";
import {useMutation} from "react-query";
import {useLocaleContext} from "../commons/translations/LocaleContext";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {ShareAltOutlined} from "@ant-design/icons";
import DonateIcon from "../commons/icons/DonateIcon";
import {Link} from "react-router-dom";
import {Label} from "../commons/translations/Label";
import {ReservationDTO} from "./types";
import {PublicEventDTO, PublicTableDTO} from "../home/types";
import dayjs from "dayjs";
import {useEventShare} from "../commons/utils";

export type LauncherReservationConfirmationFormProps = {
    table: PublicTableDTO;
    event: PublicEventDTO;
}

type ReservationRequestDTO = {
    userId: string;
    seats: number;
    locale: string;
    captcha: string;
}

export const LauncherReservationConfirmationForm : React.FC<LauncherReservationConfirmationFormProps> = (props) => {
    const { table, event } = props;
    const { locale } = useLocaleContext();
    const { user } = useAuthContext();
    const [form] = Form.useForm<{plusOne: boolean}>();
    const { t } = useTranslation();
    const [plusOne, setPlusOne] = React.useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [reservation, setReservation] = React.useState<ReservationDTO | undefined>(undefined);
    const canReservePlusOne = table.seatsMax - table.reserved >= 2;

    const mutation = useMutation({
        mutationFn: async (data: ReservationRequestDTO) => {
            const r = await axios.post<object, AxiosResponse<ReservationDTO>>(`/api/reservations/${table.id}`, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            });
            return r.data;
        },
        onSuccess: (rsv) => {
            setReservation(rsv);
            setShowConfirmation(true);
            message.success(t('reservation.success'));
        },
        onError: () => {
            message.error(t('reservation.error'));
        }
    });

    const onFinish = () => {
        if (!user || !executeRecaptcha) return;

        const request: ReservationRequestDTO = {
            userId: user.id,
            seats: plusOne ? 2 : 1,
            locale: locale!.locale,
            captcha: ''
        };
        executeRecaptcha('reservation')
        .then(captcha => {
            const finalData = {
                ...request,
                captcha
            }
            mutation.mutate(finalData);
        })
    };

    const { copyTableSharePath } = useEventShare(event, table);

    return (
        <>
            <LoadingOverlay working={mutation.isLoading} />
            <Modal open={showConfirmation} closable={false} footer={null}>
                <div style={{padding: '2.5%'}}>
                    <Flex justify={"center"} align={"center"} vertical={true}>
                        <Alert type={"success"} showIcon message={<Typography.Text strong>{t('reservation.confirmation')}</Typography.Text>} description={
                            <Typography.Text><Label trl={'reservation.confirmation.details'}
                                                    segments={[
                                                        {key: '#0', value: mutation.data?.seats.toString() || '1'},
                                                        {key: '#1', value: event.location},
                                                        {key: '#2', value: dayjs(event.startsAt).format('DD/MM/YYYY HH:mm')},
                                                        {key: '#3', value: table.game?.title || ''},
                                                        {key: '#4', value: table.facilitator},
                                                    ]}
                            /></Typography.Text>
                        }/>
                        <br/>
                        <Label trl={'reservation.donation.prompt'} />
                        <br/>
                        <Button size="large" icon={<DonateIcon/>} type={"primary"}>
                            <Link target={"_blank"}
                                  to={'https://buymeacoffee.com/therpglodge'}>{t('reservation.donation.action')}</Link>
                        </Button>
                        <Divider />
                        <br/>

                        <Typography.Text><Label trl={'reservation.confirmation.rsvp'} /></Typography.Text>
                        <br/>
                        <Alert type="info" showIcon message={t('reservation.confirmation.cancellation')} description={
                            <Link to={`/my-reservations/${reservation?.id}`}>
                                <Button type={"primary"} size={"large"} style={{width: '100%'}}>{t('reservation.manage')}</Button>
                            </Link>
                        }
                        />
                        <br/>
                        <p style={{marginTop: '5px'}}>
                            <Button size={"large"} style={{width: '100%'}} icon={<ShareAltOutlined/>} onClick={copyTableSharePath}>{t('tableCard.buttons.share')}</Button>
                        </p>
                    </Flex>
                </div>
            </Modal>
            {!showConfirmation && !mutation.isLoading &&
                <Card>
                    <Form form={form} {...defaultFormItemLayout} onFinish={onFinish}>
                        {canReservePlusOne &&
                            <Form.Item name="plusOne" valuePropName="checked" label={<Label trl="reservation.form.plusOne" />}>
                                <Switch onChange={(checked) => setPlusOne(checked)}/>
                            </Form.Item>
                        }
                        <Button type={"primary"}
                                onClick={form.submit}
                                style={{width: '100%', backgroundColor: 'limegreen'}}>
                            {t('reservation.submit.seats') + (plusOne ? '2' : '1')}
                        </Button>
                    </Form>
                </Card>
            }
        </>
    )
}